class MCartDrawer extends HTMLElement {
  constructor() {
    super();
    this.getSectionToRenderListener = this.getSectionToRender.bind(this);
  }

  get sectionName() {
    return 'cart-drawer';
  }

  get cartDrawerInner() {
    return this.querySelector(".m-cart-drawer__inner");
  }

  get cartDrawerCloseIcon() {
    return this.querySelector(".m-cart-drawer__close");
  }

  getSectionToRender(event) {
    event.detail.sections.push(this.sectionName);
  }

  connectedCallback() {
    document.addEventListener('cart:grouped-sections', this.getSectionToRenderListener);

    this.setHeaderCartIconAccessibility();
    this.addEventListener("click", (event) => {
      if (event.target.closest(".m-cart-drawer__inner") !== this.cartDrawerInner || event.target === this.cartDrawerCloseIcon) {
        this.close();
      }
    });
  }

  disconnectedCallback() {
    document.removeEventListener('cart:grouped-sections', this.getSectionToRenderListener);
  }

  setHeaderCartIconAccessibility() {
    const cartLinks = document.querySelectorAll(".m-cart-icon-bubble");
    cartLinks.forEach((cartLink) => {
      cartLink.setAttribute("role", "button");
      cartLink.setAttribute("aria-haspopup", "dialog");
      cartLink.addEventListener("click", (event) => {
        if (MinimogSettings.enable_cart_drawer) {
          event.preventDefault();
          this.open(cartLink);
        }
      });
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    this.classList.add("m-cart-drawer--active");
    document.documentElement.classList.add("prevent-scroll");
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.style.setProperty("--m-bg-opacity", "0.5");
        this.style.setProperty("--translate-x", "0");
        window.MinimogEvents.emit(MinimogTheme.pubSubEvents.openCartDrawer);
      });
    });
  }

  close() {
    this.style.setProperty("--m-bg-opacity", "0");
    this.style.setProperty("--translate-x", "100%");
    setTimeout(() => {
      this.classList.remove("m-cart-drawer--active");
      document.documentElement.classList.remove("prevent-scroll");
    }, 300);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define("m-cart-drawer", MCartDrawer);

class MCartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener("click", (event) => {
      event.preventDefault();
      const cartItems = this.closest("m-cart-items");
      cartItems.updateQuantity(this.dataset.index, 0);
    });
  }
}

customElements.define("m-cart-remove-button", MCartRemoveButton);

class MCartTemplate extends HTMLElement {
  constructor() {
    super();
    document.addEventListener('cart:grouped-sections', this.getSectionToRender.bind(this));
  }

  get sectionName() {
    return 'cart-template';
  }

  getSectionToRender(event) {
    event.detail.sections.push(this.sectionName);
  }
}

customElements.define("m-cart", MCartTemplate);

class MCartItems extends HTMLElement {
  cartUpdateUnsubscriber = undefined;

  constructor() {
    super();

    window.FoxKitSections = [this.sectionName, this.cartCountSectionName];
    this.rootUrl = window.Shopify.routes.root;

    this.onCartRefreshListener = this.onCartRefresh.bind(this);
    document.addEventListener("cart:refresh", this.onCartRefreshListener);

    this.addEventListener('change', debounce(this.onChange.bind(this), 300));
    this.cartUpdateUnsubscriber = MinimogEvents.subscribe(MinimogTheme.pubSubEvents.cartUpdate, this.onCartUpdate.bind(this));
  }

  connectedCallback() {
    this.isCartPage = MinimogSettings.templateName === "cart";
    this.cartDrawerInner = document.querySelector(".m-cart-drawer__inner");
    let loadingTarget = this.cartDrawerInner;
    if (this.isCartPage) loadingTarget = document.body;
    this.loading = new MinimogLibs.AnimateLoading(loadingTarget, { overlay: loadingTarget });
  }

  get sectionName() {
    return this.dataset.sectionName || 'cart-template';
  }

  get cartCountSectionName() {
    return 'cart-count';
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }

    document.removeEventListener("cart:refresh", this.onCartRefreshListener);
  }

  onChange(event) {
    const { target } = event;
    if (target.closest('m-quantity-input')) {
      this.updateQuantity(
        target.dataset.index,
        target.value,
        document.activeElement.getAttribute('name'),
        target
      );
    }
  }

  onCartError(errors, target, line) {
    if (target) {
      const quantityInput = target.closest('m-quantity-input');
      quantityInput.setValidity(errors);
    } else {
      window.location.href = MinimogSettings.routes.cart;
    }

    this.updateLiveRegions(line, errors);
  }

  async onCartRefresh(event) {
    const cartSelector = this.isCartPage ? 'm-cart' : 'm-cart-drawer';
    const cartElement = document.querySelector(cartSelector);
    const cartCount = document.querySelectorAll('m-cart-count');
    try {
      await fetch(`${this.rootUrl}?sections[]=${this.sectionName}&sections[]=${this.cartCountSectionName}`)
        .then((response) => response.text())
        .then((responseText) => {
          const parseJSON = JSON.parse(responseText);

          // Refresh Cart
          this.getSectionsToRender().forEach((section) => {
            const elementToReplace =
              document.getElementById(section.id)?.querySelector(section.selector) || document.getElementById(section.id);
            if (elementToReplace) elementToReplace.innerHTML = this.getSectionInnerHTML(parseJSON[this.sectionName], section.selector);
          });

          // Update cart count
          const responseCartCount = parseJSON[this.cartCountSectionName];
          const newCartCount = new DOMParser().parseFromString(responseCartCount, "text/html");
          cartCount.forEach(cart => cart.onUpdate(newCartCount));
        });

      if (event.detail?.open === true) {
        if (!this.isCartPage) cartElement.open();
      }
    } catch (error) {
      console.error("Error refreshing cart:", error);
    }
  }

  onCartUpdate(event) {
    this.getSectionsToRender().forEach((section) => {
      const elementToReplace = document.getElementById(section.id)?.querySelector(section.selector);
      if (elementToReplace) elementToReplace.innerHTML = this.getSectionInnerHTML(event.cart.sections[this.sectionName], section.selector);
    });

    document.dispatchEvent(
      new CustomEvent('cart:updated', {
        detail: {
          cart: event.cart,
        },
      })
    );
  }

  updateQuantity(line, quantity, name, target) {
    this.loading.start();

    const { routes } = window.MinimogSettings;

    let sectionsToBundle = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
    );

    const body = JSON.stringify({
      line,
      quantity,
      sections: sectionsToBundle,
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => response.json())
      .then((response) => {

        if (response.errors) {
          this.loading.finish();
          this.onCartError(response.errors, target, line);
          return;
        }

        window.MinimogEvents.emit(MinimogTheme.pubSubEvents.cartUpdate, { cart: response });
      })
      .catch((error) => {
        console.log(error);
      }).finally(() => {
        this.loading.finish();
      });
  }

  updateLiveRegions(line, message) {
    let lineItemNode = document.getElementById(`MinimogCart-Item-${line}`);
    if (message !== "" && lineItemNode) {
      MinimogTheme.Notification.show({
        target: lineItemNode,
        type: "warning",
        message: message,
      });
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'MinimogCartDrawer',
        selector: '#MinimogCartDrawerHeader',
      },
      {
        id: 'MinimogCartDrawer',
        selector: '#MinimogCartDrawerBody',
      },
      {
        id: 'MinimogCartDrawer',
        selector: '#MinimogCartDrawerFooter',
      },
      {
        id: 'MinimogCart',
        selector: '#MinimogCartHeader',
      },
      {
        id: 'MinimogCart',
        selector: '#MinimogCartBody',
      },
      {
        id: 'MinimogCart',
        selector: '#MinimogCartFooter',
      },
    ];
  }
}

customElements.define("m-cart-items", MCartItems);


/* Cart Addons */
if (!customElements.get("m-cart-addons")) {
  class MCartAddons extends HTMLElement {
    constructor() {
      super();
      this.selectors = {
        zipCode: '[name="address[zip]"]',
        province: '[name="address[province]"]',
        country: '[name="address[country]"]',
        addressForm: '[data-address="root"]',
        shippingMessage: ".m-cart-addon__shipping-rate",
        cartNote: '[name="note"]',
        saveAddonButton: ".m-cart-addon--save",
        closeAddonButton: ".m-cart-addon--close",
        calcShippingButton: ".m-cart-addon--calculate",
        triggerAddonButton: ".m-cart-addon--trigger-button",
        devliveryTime: '[name="attributes[Delivery time]"]',
      };
    }

    connectedCallback() {
      this.cartWrapper = document.querySelector(".m-cart-drawer");
      this.isCartPage = MinimogSettings.templateName === "cart";
      if (this.isCartPage) {
        this.cartWrapper = document.querySelector(".m-cart__footer--wrapper");
      }
      this.initAddress = false;
      this.cartOverlay = this.cartWrapper.querySelector(".m-cart__overlay");
      this.domNodes = queryDomNodes(this.selectors, this);
      this.rootUrl = window.Shopify.routes.root;
      this.discountCodeKey = "minimog-discount-code";
      this.deliveryCodeKey = "minimog-delivery-code";
      this.init();
    }

    disconnectedCallback() {
      if (this._removeCloseAddonButton) this._removeCloseAddonButton();
      if (this._removeCalcShippingButton) this._removeCalcShippingButton();
      this.querySelectorAll(this.selectors.triggerAddonButton).forEach((button) => {
        button.removeEventListener('click', this.handleOpenAddon.bind(this));
      });
      this.querySelectorAll(this.selectors.saveAddonButton).forEach((button) => {
        button.removeEventListener('click', this.handleSaveAddonValue.bind(this));
      });
    }

    init() {
      const { devliveryTime } = this.domNodes;

      this.querySelectorAll(this.selectors.triggerAddonButton).forEach((button) => {
        button.addEventListener('click', this.handleOpenAddon.bind(this));
      });

      this.querySelectorAll(this.selectors.saveAddonButton).forEach((button) => {
        button.addEventListener('click', this.handleSaveAddonValue.bind(this));
      });

      this._removeCloseAddonButton = addEventDelegate({
        selector: this.selectors.closeAddonButton,
        context: this.cartWrapper,
        handler: this.close.bind(this),
      });

      this._removeCalcShippingButton = addEventDelegate({
        selector: this.selectors.calcShippingButton,
        context: this.cartWrapper,
        handler: this.calcShipping.bind(this),
      });

      if (devliveryTime) {
        const code = localStorage.getItem(this.deliveryCodeKey);
        if (code) devliveryTime.value = code;
      }

      const shippingPanel = this.querySelector('#m-addons-shipping');
      if (shippingPanel) this.initCepInput(shippingPanel);

      const today = new Date().toISOString().slice(0, 16);
      const deliveryTimeElm = this.querySelector("#delivery-time");
      if (deliveryTimeElm) deliveryTimeElm.min = today;
    }

    handleOpenAddon(e) {
      e.preventDefault();
      const { target } = e;

      if (this.isCartPage) {
        const addonCurrentActive = document.querySelector(".m-cart-addon__body.open");
        if (addonCurrentActive) addonCurrentActive.classList.remove("open");
      }
      const { open: addonTarget } = target.dataset;
      const addonNode = this.cartWrapper.querySelector(`#m-addons-${addonTarget}`);
      this.removeActiveAllButton();
      target.classList.add("active");
      addonNode && addonNode.classList.add("open");
      this.cartOverlay && this.cartOverlay.classList.add("open");
      this.openAddon = addonNode;

      if (addonTarget === "shipping") {
        this.initCepInput(addonNode);
      }
    }

    initCepInput(addonNode) {
      const zipInput = addonNode?.querySelector(this.selectors.zipCode);
      if (!zipInput || zipInput.dataset.cepMask === "1") return;
      zipInput.dataset.cepMask = "1";
      zipInput.addEventListener("input", (e) => {
        const v = e.target.value.replace(/\D/g, "");
        e.target.value = v.length > 5 ? `${v.slice(0, 5)}-${v.slice(5, 8)}` : v;
      });
      zipInput.addEventListener("keydown", (e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          const calcBtn = addonNode.querySelector(this.selectors.calcShippingButton);
          if (calcBtn) this.calcShipping({ preventDefault: () => {}, target: calcBtn });
        }
      });
    }

    removeActiveAllButton() {
      const triggerButtons = this.querySelectorAll(this.selectors.triggerAddonButton);
      triggerButtons && triggerButtons.forEach((button) => button.classList.remove("active"));
    }

    setupCountries() {
      if (this.initAddress) return;
      if (Shopify && Shopify.CountryProvinceSelector) {
        new Shopify.CountryProvinceSelector("AddressCountry", "AddressProvince", {
          hideElement: "AddressProvinceContainer",
        });
        this.initAddress = true;
      }
    }

    close(event) {
      event.preventDefault();
      this.openAddon.classList.remove("open");
      this.cartOverlay && this.cartOverlay.classList.remove("open");
      this.removeActiveAllButton();
      this.openAddon = null;
    }

    async calcShipping(event) {
      event.preventDefault();
      const calcBtn = event.target.closest(".m-cart-addon--calculate");
      if (!calcBtn) return;
      calcBtn.classList.add("m-spinner-loading");
      const zipRaw = this.domNodes.zipCode?.value?.trim() || "";
      const cepUnmasked = zipRaw.replace(/\D/g, "");
      const country = "BR";
      let province = "BR";

      const frete = window.JvEstimativaFrete;
      const resultsEl = this.domNodes.shippingMessage;
      resultsEl.classList.remove("error");
      if (frete?.hideAllFreteAlerts) {
        frete.hideAllFreteAlerts(resultsEl);
      } else {
        resultsEl.innerHTML = "";
      }

      const { noShippingRate, zipcodeValidate } = MinimogStrings;

      if (!/^\d{8}$/.test(cepUnmasked)) {
        calcBtn.classList.remove("m-spinner-loading");
        const msg = zipcodeValidate || "Formato de CEP inválido.";
        if (frete?.showFreteMessage) frete.showFreteMessage(resultsEl, "danger", msg);
        else resultsEl.innerHTML = `<p>${msg}</p>`;
        return;
      }

      try {
        try {
          const respCep = await fetch(`https://brasilapi.com.br/api/cep/v1/${cepUnmasked}`);
          if (respCep.ok) {
            const data = await respCep.json();
            province = data.state || "BR";
            if (this.domNodes.province) this.domNodes.province.value = province;
          }
        } catch (_) {}

        if (this.domNodes.country) this.domNodes.country.value = country;

        let res = await this.fetchShippingRates(cepUnmasked, country, province);

        if (!res?.shipping_rates?.length) {
          res = await this.fetchShippingRatesAlternative(cepUnmasked, country, province);
        }

        if (res && res.shipping_rates) {
          if (res.shipping_rates.length > 0 && frete?.renderRates) {
            frete.renderRates(res, resultsEl);
          } else if (res.shipping_rates.length > 0) {
            resultsEl.innerHTML = res.shipping_rates
              .map((rate) => `<p>${rate.name}: ${rate.price}</p>`)
              .join("");
          } else if (frete?.showFreteMessage) {
            frete.showFreteMessage(resultsEl, "warning", noShippingRate);
          } else {
            resultsEl.innerHTML = `<p>${noShippingRate}</p>`;
          }
        } else if (res && typeof res === "object") {
          const messages = Object.entries(res)
            .map((error) => `${error[1][0]}`)
            .join(" ");
          resultsEl.classList.add("error");
          if (frete?.showFreteMessage) frete.showFreteMessage(resultsEl, "danger", messages);
          else resultsEl.innerHTML = `<p>${messages}</p>`;
        }
      } catch (e) {
        console.error(e);
        if (frete?.showFreteMessage) {
          frete.showFreteMessage(resultsEl, "danger", "Erro ao calcular frete. Tente novamente.");
        } else {
          resultsEl.innerHTML = `<p>Erro ao calcular frete. Tente novamente.</p>`;
        }
      } finally {
        calcBtn.classList.remove("m-spinner-loading");
      }
    }

    async fetchShippingRates(zip, country, province) {
      const r = await fetch(
        `${this.rootUrl}cart/shipping_rates.json?shipping_address%5Bzip%5D=${zip}&shipping_address%5Bcountry%5D=${country}&shipping_address%5Bprovince%5D=${province}`,
        { headers: { "X-Requested-With": "XMLHttpRequest" } }
      );
      if (!r.ok) return null;
      return r.json();
    }

    async fetchShippingRatesAlternative(zip, country, province) {
      const form = new URLSearchParams();
      form.append("shipping_address[zip]", zip);
      form.append("shipping_address[country]", country);
      form.append("shipping_address[province]", province);
      const prep = await fetch(`${this.rootUrl}cart/prepare_shipping_rates`, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          "X-Requested-With": "XMLHttpRequest",
        },
        body: form,
      });
      if (!prep.ok) return null;
      for (let i = 0; i < 10; i++) {
        await new Promise((r) => setTimeout(r, 1000));
        const res = await fetch(`${this.rootUrl}cart/async_shipping_rates`, {
          headers: { "X-Requested-With": "XMLHttpRequest" },
        });
        if (res.status === 200) {
          const json = await res.json();
          if (json?.shipping_rates?.length) return json;
        }
      }
      return null;
    }

    handleSaveAddonValue(e) {
      e.preventDefault();
      const { target } = e;
      const { devliveryTime } = this.domNodes;
      if (target.dataset.action === "note") {
        this.updateCartNote();
        this.close(e);
      }
      if (target.dataset.action === "delivery") {
        const code = devliveryTime.value;
        const isValidDate = Date.parse(code);
        if (isValidDate > Date.now()) {
          localStorage.setItem(this.deliveryCodeKey, code);
          this.close(e);
        } else {
          localStorage.setItem(this.deliveryCodeKey, "");
          devliveryTime.value = "";
          window.MinimogTheme.Notification.show({
            target: this.querySelector(".m-cart-addon-message-error"),
            method: "appendChild",
            type: "error",
            message: window.MinimogStrings.valideDateTimeDelivery,
            last: 3000,
            sticky: false,
          });
        }
      }
    }

    updateCartNote() {
      const cartNoteValue = this.domNodes.cartNote.value;
      const body = JSON.stringify({ note: cartNoteValue });
      fetch(`${window.MinimogSettings.routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
    }
  }
  customElements.define("m-cart-addons", MCartAddons);
}

class MCartDiscount extends HTMLDivElement {
  constructor() {
    super();

    this.submitEl.addEventListener('click', this.handleFormSubmit.bind(this));
    this.couponEl.addEventListener('keydown', this.handleInputKey.bind(this));
  }

  get submitEl() {
    return (this._submitEl = this._submitEl || this.querySelector('[type="submit"]'));
  }

  get messageEl() {
    return (this._messageEl = this._messageEl || this.querySelector('.m-cart-addon-message-error'));
  }

  get couponEl() {
    return (this._couponEl = this._couponEl || this.querySelector('input[name="discount_code"]')); 
  }

  get cartAddonDrawer() {
    return this.closest('.m-cart-addon');
  }

  get cartDiscountsEl() {
    return document.querySelector('[data-minimog-cart-discounts]');
  }

  getDiscounts() {
    const discounts = [];

    if (this.cartDiscountsEl) {
      const items = this.cartDiscountsEl.querySelectorAll('.m-cart__discount--item');
      items &&
        items.forEach((item) => {
          discounts.push(item.dataset.discountCode);
        });
    }

    return discounts;
  }
 
  handleInputKey(event) {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.handleFormSubmit(event);
    }
  }

  handleFormSubmit(event) {
    event.preventDefault();

    if (this.submitEl.getAttribute('aria-disabled') === 'true') return;

    this.displayFormErrors();
    
    const newDiscountCode = this.couponEl.value;

    if( !this.couponEl.checkValidity() ) {
      this.couponEl.reportValidity();
      return;
    }

    const discounts = this.getDiscounts();

    if (discounts.includes(newDiscountCode)) {
      this.displayFormErrors(MinimogStrings.duplicateDiscountError);
      return;
    }

    discounts.push(newDiscountCode);

    let sectionsToBundle = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
    );

    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    const formData = new FormData();
    formData.append('sections', sectionsToBundle);
    formData.append('sections_url', window.location.pathname);
    formData.append('discount', discounts.join(','));

    config.body = formData;

    this.submitEl.setAttribute('aria-disabled', 'true');
    this.submitEl.classList.add('m-spinner-loading');

    fetch(MinimogSettings.routes.cart_update_url, config)
      .then((response) => response.json())
      .then(async (parsedState) => {
        if (
          parsedState.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
            return discount.code === newDiscountCode && discount.applicable === false;
          })
        ) {
          this.couponEl.value = '';
          this.displayFormErrors(MinimogStrings.applyDiscountError);
          return;
        }

        if (this.cartAddonDrawer) {
          this.cartAddonDrawer.close(event);
        }

        const cartJson = await (await fetch(`${MinimogSettings.routes.cart}`, { ...fetchConfig() })).json();
        cartJson['sections'] = parsedState['sections'];

        this.updateCartState(cartJson);
      })
      .catch((e) => {
        console.error(e);
      })
      .finally(() => {
        this.submitEl.removeAttribute('aria-disabled');
        this.submitEl.classList.remove('m-spinner-loading');
      });
  }

  updateCartState = (cartJson) => {
    window.MinimogEvents.emit(MinimogTheme.pubSubEvents.cartUpdate, { cart: cartJson });
  };

  displayFormErrors = (errorMessage = false) => {
    if (!this.messageEl) {
      if (errorMessage !== false) {
        alert(errorMessage);
      }
    } else {
      this.messageEl.classList.toggle('!m:hidden', !errorMessage);
      if (errorMessage !== false) {
        this.messageEl.innerText = errorMessage;
      }
    }
  };
}
customElements.define('m-cart-discount', MCartDiscount, { extends: 'div' });

class MCartDiscountRemove extends HTMLButtonElement {
  constructor() {
    super();

    this.selectors = {
      list: '.m-cart__discount',
      item: '.m-cart__discount--item',
    };

    this.clickHandler = this.handleClick.bind(this);
  }

  connectedCallback() {
    this.listEl = this.closest(this.selectors.list);

    this.addEventListener('click', this.clickHandler);
  }

  disconnectedCallback() {
    this.removeEventListener('clikc', this.clickHandler);
  }

  handleClick(event) {
    event.preventDefault();
    if (this.getAttribute('aria-disabled') === 'true') return;

    this.setAttribute('aria-disabled', 'true');
    this.classList.add('m-spinner-loading');

    this.discounts = [];

    const thisItem = this.closest('li');
    const items = this.listEl.querySelectorAll(this.selectors.item);
    items &&
      items.forEach((item) => {
        if (item != thisItem) {
          this.discounts.push(item.dataset.discountCode);
        }
      });

    this.updateCartDiscounts();
  }

  updateCartDiscounts() {
    let sectionsToBundle = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
    );

    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    const formData = new FormData();
    formData.append('sections', sectionsToBundle);
    formData.append('sections_url', window.location.pathname);
    formData.append('discount', this.discounts.join(','));

    config.body = formData;

    fetch(MinimogSettings.routes.cart_update_url, config)
      .then((response) => response.json())
      .then(async (parsedState) => {
        const cartJson = await (await fetch(`${MinimogSettings.routes.cart}`, { ...fetchConfig() })).json();
        cartJson['sections'] = parsedState['sections'];

        this.updateCartState(cartJson);
      })
      .catch((e) => {
        console.error(e);
      });
  }

  updateCartState = (cartJson) => {
    window.MinimogEvents.emit(MinimogTheme.pubSubEvents.cartUpdate, { cart: cartJson });
  };
}
customElements.define('m-cart-discount-remove', MCartDiscountRemove, { extends: 'button' });

class MCartForm extends HTMLFormElement {
  constructor() {
    super();
    
    this.addEventListener('submit', this.handleSubmit.bind(this));
  }

  /**
   * Compatible with Foxkit Discount Code
   * This input auto append by Foxkit that make lost discounts when submit form.
   * We need re-add discounts to the input before submit form.
   */
  get discountInput() {
     return this.querySelector('input[name="discount"]');
  }

  get cartDiscountsEl() {
    return document.querySelector('[data-minimog-cart-discounts]');
  }

  getDiscounts() {
    const discounts = [];

    if (this.cartDiscountsEl) {
      const items = this.cartDiscountsEl.querySelectorAll('.m-cart__discount--item');
      items &&
        items.forEach((item) => {
          discounts.push(item.dataset.discountCode);
        });
    }

    return discounts;
  }

  handleSubmit() {
    if(this.discountInput) {
      const discounts = this.getDiscounts();

      if(discounts.length > 0) {
        const foxkitDiscount  = this.discountInput.value;

        if(foxkitDiscount !== '') {
          discounts.push(foxkitDiscount);
        }

        this.discountInput.value = discounts.join(',');
      }
    }
  }
}
customElements.define('m-cart-form', MCartForm, { extends: 'form' });